"use client"

import { useState } from "react"
import { deleteAddressAction } from "@/actions/userActions"
import { EditIcon, TrashIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { UserData } from "@/lib/intershop/interfaces/user/user"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import { cn } from "@/components/ui/helpers/utils"
import AddAddressButton from "@/components/common/addresses/AddAddressButton"
import AddressEditModal from "@/components/common/addresses/AddressEditModal"
import { AddressFields } from "@/components/common/addresses/AddressFields"
import BasicCard from "@/components/common/BasicCard"

interface AccountAddressesClientProps {
  addresses: ExtendedAddressRO[]
  customer: any
  user: any
  isB2B: boolean
}

export default function AccountAddressesClient({ addresses, customer, user, isB2B }: AccountAddressesClientProps) {
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [addressToEdit, setAddressToEdit] = useState<ExtendedAddressRO | null>(null)
  const t = useTranslations("storefront")
  const [localAddresses, setLocalAddresses] = useState(addresses)

  const handleEdit = (address: ExtendedAddressRO) => {
    setAddressToEdit(address)
    setEditModalVisible(true)
  }

  const handleDelete = (address: ExtendedAddressRO) => {
    if (address?.id && window.confirm(t("confirm_delete_address"))) {
      deleteAddressAction(customer.customerNo, address.id)
      const updatedAddresses = localAddresses.filter((a) => a.id !== address.id)
      setLocalAddresses(updatedAddresses)
    }
  }

  const handleSave = (newAddress: ExtendedAddressRO, newUser: UserData) => {
    setLocalAddresses((prev) => prev.map((a) => (a.id === newAddress.id ? newAddress : a)))
    setEditModalVisible(false)
  }

  return (
    <>
      <div className="mb-10 flex flex-row items-center justify-between">
        <h1 className="text-2xl font-bold">{t("addresses.title")}</h1>
        <AddAddressButton
          customer={customer}
          user={user}
          type={"general"}
          onAdd={(address, user) => setLocalAddresses((prev) => [...prev, address])}
        />
      </div>
      <div className="flex flex-row flex-wrap justify-start md:gap-2">
        {localAddresses && localAddresses.length > 0 && (
          <>
            {localAddresses.map((address) => (
              <BasicCard key={address.id} className={"flex min-h-0 w-full min-w-64 flex-row gap-x-6 md:w-auto"}>
                <AddressFields address={address} isB2B={isB2B} />
                <div className={cn("flex w-full flex-col items-end gap-y-2")}>
                  <button onClick={() => handleEdit(address)}>
                    <EditIcon className="size-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(address)}
                    className="text-destructive hover:text-destructive/80 mt-2"
                    title="Delete address"
                  >
                    <TrashIcon className="size-5" />
                  </button>
                </div>
              </BasicCard>
            ))}
          </>
        )}
      </div>
      <AddressEditModal
        visible={editModalVisible}
        address={addressToEdit}
        customer={customer}
        user={user}
        mode="edit"
        onCancel={() => setEditModalVisible(false)}
        onSave={(a, u) => handleSave(a, u)}
      />
    </>
  )
}
